/* ============================================================
   Trendout — main.js
   Global behaviour: announcement rotator, sticky header, mobile
   menu, cart (localStorage) + drawer, scroll reveal, dialogs,
   product-card quick add / wishlist, homepage countdown +
   sold counter, newsletter stub.
   Exposes a single namespace: window.Trendout
   ============================================================ */
(function () {
  'use strict';

  var $ = function (sel, root) { return (root || document).querySelector(sel); };
  var $$ = function (sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };

  /* ---------- Money ---------- */
  function formatRs(n) { return 'Rs. ' + Math.round(n).toLocaleString('en-US'); }
  function parseRs(str) { return parseInt(String(str).replace(/[^0-9]/g, ''), 10) || 0; }

  /* ---------- Safe storage (file:// in some browsers blocks it) ---------- */
  var mem = {};
  var store = {
    get: function (k) {
      try { return window.localStorage.getItem(k); } catch (e) { return mem[k] || null; }
    },
    set: function (k, v) {
      try { window.localStorage.setItem(k, v); } catch (e) { mem[k] = v; }
    },
    remove: function (k) {
      try { window.localStorage.removeItem(k); } catch (e) { delete mem[k]; }
    }
  };

  /* ============================================================
     Dialog manager — focus trap, Escape, scrim click, focus return
     ============================================================ */
  var FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
  var openDialogs = [];

  function openDialog(overlay, opts) {
    opts = opts || {};
    var trigger = document.activeElement;
    overlay.hidden = false;
    document.body.classList.add('dialog-open');

    function focusables() {
      return $$(FOCUSABLE, overlay).filter(function (el) { return el.offsetParent !== null || el === document.activeElement; });
    }
    function onKeydown(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key !== 'Tab') return;
      var els = focusables();
      if (!els.length) return;
      var first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
    function close() {
      overlay.hidden = true;
      overlay.removeEventListener('keydown', onKeydown);
      var i = openDialogs.indexOf(entry);
      if (i > -1) openDialogs.splice(i, 1);
      if (!openDialogs.length) document.body.classList.remove('dialog-open');
      if (opts.onClose) opts.onClose();
      if (trigger && trigger.focus) trigger.focus();
    }
    var entry = { overlay: overlay, close: close };
    openDialogs.push(entry);
    overlay.addEventListener('keydown', onKeydown);
    $$('[data-dialog-close]', overlay).forEach(function (el) {
      el.onclick = close;
    });
    var scrim = $('.overlay__scrim', overlay);
    if (scrim) scrim.onclick = close;
    var target = $('[data-autofocus]', overlay) || focusables()[0];
    if (target) target.focus();
    return { close: close };
  }

  /* ============================================================
     Cart — the real Shopify cart, via the Cart AJAX API
     ============================================================
     The static build kept an array in localStorage seeded with two demo products.
     Nothing of that survives here. First paint of the drawer is server-rendered by
     sections/trendout-cart-drawer.liquid; after every mutation we re-request that same
     section and swap its markup in, so server output and JS-refreshed output can never
     drift apart. Line markup therefore exists in exactly one place — the Liquid.
     ============================================================ */
  var T = window.Trendout || {};
  var cartRoutes = T.routes || {};
  var CART_SECTION = 'trendout-cart-drawer';

  /* Mirrors Liquid's `money` filter so JS-updated figures match server-rendered ones
     exactly — currency symbol, decimals and separators all follow the store setting
     instead of the hardcoded "Rs." the static build used. */
  function formatMoney(cents) {
    var format = T.moneyFormat || '${{amount}}';
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    var match = format.match(placeholder);
    if (!match) return format;

    function withDelimiters(number, precision, thousands, decimal) {
      if (isNaN(number) || number === null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return whole + (parts[1] ? decimal + parts[1] : '');
    }

    var value;
    switch (match[1]) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0, ',', '.'); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      default: value = withDelimiters(cents, 2, ',', '.');
    }
    return format.replace(placeholder, value);
  }

  function setCartBusy(busy) {
    var drawer = $('#cart-drawer');
    if (drawer) drawer.classList.toggle('is-busy', !!busy);
  }

  function bounceCartButtons() {
    $$('[data-cart-open]').forEach(function (btn) {
      btn.classList.remove('is-bouncing');
      void btn.offsetWidth; /* restart animation */
      btn.classList.add('is-bouncing');
    });
  }

  /* Kept for compatibility with the other page scripts, which call it after their own
     mutations. Counts now come from the server rather than a local array. */
  function renderCartBadges(bounce) {
    if (bounce) bounceCartButtons();
  }

  function bindCartLines() {
    $$('[data-cart-remove]').forEach(function (btn) {
      btn.onclick = function () { changeLine(btn.getAttribute('data-cart-remove'), 0); };
    });
  }

  function refreshCart(openAfter) {
    var url = window.location.pathname + '?sections=' + CART_SECTION;
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data[CART_SECTION];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');

        var newBody = doc.querySelector('[data-cart-body]');
        var body = $('[data-cart-body]');
        if (newBody && body) body.innerHTML = newBody.innerHTML;

        var newTotal = doc.querySelector('[data-cart-total]');
        var total = $('[data-cart-total]');
        if (newTotal && total) total.textContent = newTotal.textContent.trim();

        var newCount = doc.querySelector('[data-cart-count]');
        if (newCount) {
          var count = newCount.textContent.trim();
          $$('[data-cart-count]').forEach(function (el) { el.textContent = count; });
        }

        var newBtn = doc.querySelector('#cart-drawer-form [name="checkout"]');
        var btn = $('#cart-drawer-form [name="checkout"]');
        if (newBtn && btn) btn.disabled = newBtn.hasAttribute('disabled');

        bindCartLines();
      })
      .catch(function () { /* leave the drawer showing its last good state */ })
      .then(function () { if (openAfter) openCartDrawer(); });
  }

  function changeLine(line, qty) {
    setCartBusy(true);
    return fetch(cartRoutes.cartChange || '/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: qty })
    })
      .then(function () { return refreshCart(); })
      .catch(function () {})
      .then(function () { setCartBusy(false); });
  }

  /* variantId is a Shopify variant id. The static build passed a title/price/size
     object; callers were updated with the templates. */
  function addToCart(variantId, qty, opts) {
    opts = opts || {};
    if (!variantId) return Promise.resolve();
    setCartBusy(true);

    var item = { id: Number(variantId), quantity: qty || 1 };
    if (opts.properties) item.properties = opts.properties;
    if (opts.sellingPlan) item.selling_plan = opts.sellingPlan;

    return fetch(cartRoutes.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [item] })
    })
      .then(function (r) {
        if (!r.ok) { return r.json().then(function (e) { throw e; }); }
        return r.json();
      })
      .then(function () { return refreshCart(opts.openDrawer); })
      .then(function () { bounceCartButtons(); })
      .catch(function (err) {
        var body = $('[data-cart-body]');
        var msg = (err && (err.description || err.message)) || 'Sorry, that could not be added to your cart.';
        if (body) body.insertAdjacentHTML('afterbegin', '<div class="cart-note cart-note--error" role="alert">' + msg + '</div>');
        openCartDrawer();
      })
      .then(function () { setCartBusy(false); });
  }

  /* Adds several variants in one request — used by the bundle "Add set" buttons, where
     a set is a handful of separate products rather than one bundle product. */
  function addItems(variantIds, opts) {
    opts = opts || {};
    var items = (variantIds || [])
      .map(function (id) { return Number(String(id).trim()); })
      .filter(function (id) { return id > 0; })
      .map(function (id) { return { id: id, quantity: 1 }; });

    if (!items.length) return Promise.resolve();
    setCartBusy(true);

    return fetch(cartRoutes.cartAdd || '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (r) {
        if (!r.ok) { return r.json().then(function (e) { throw e; }); }
        return r.json();
      })
      .then(function () { return refreshCart(opts.openDrawer); })
      .then(function () { bounceCartButtons(); })
      .catch(function (err) {
        var body = $('[data-cart-body]');
        var msg = (err && (err.description || err.message)) || 'Sorry, that set could not be added to your cart.';
        if (body) body.insertAdjacentHTML('afterbegin', '<div class="cart-note cart-note--error" role="alert">' + msg + '</div>');
        openCartDrawer();
      })
      .then(function () { setCartBusy(false); });
  }

  function openCartDrawer() {
    var overlay = $('#cart-drawer');
    if (overlay) openDialog(overlay);
  }

  $$('[data-cart-open]').forEach(function (btn) {
    btn.addEventListener('click', function (e) { e.preventDefault(); openCartDrawer(); });
  });
  bindCartLines();

  /* ============================================================
     Announcement bar — rotates messages, dismissible
     ============================================================ */
  (function () {
    var bar = $('[data-announce]');
    if (!bar) return;

    var raw = bar.getAttribute('data-announce-messages') || '[]';
    var msgs;
    try {
      msgs = JSON.parse(raw);
    } catch (e) {
      msgs = [];
    }
    if (!msgs.length) return;

    /* The dismissal key carries a fingerprint of the current messages. Without it a
       shopper who closed the bar once would never see the next announcement — the
       merchant writes new copy and it silently reaches nobody. New copy, new key,
       bar returns. */
    function fingerprint(str) {
      var h = 5381;
      for (var n = 0; n < str.length; n++) {
        h = ((h << 5) + h + str.charCodeAt(n)) | 0;
      }
      return (h >>> 0).toString(36);
    }
    var dismissKey = 'trendout-announce-dismissed:' + fingerprint(raw);

    /* One-off cleanup of the pre-fingerprint key, so visitors carrying it are not
       stuck with a permanently hidden bar. */
    store.remove('trendout-announce-dismissed');

    /* Skipped in the theme editor, otherwise a merchant who dismissed the bar could
       never see it again to edit it. Skipped too when the merchant has turned
       dismissal off — there is no close button, so an old dismissal must not linger. */
    /* Dismissal is not remembered across page loads — the bar returns on the next
       navigation. Closing it is a "not now", not a permanent opt-out. */

    var msgEl = $('.announce__msg', bar);
    var speed = parseInt(bar.getAttribute('data-announce-speed'), 10) || 4200;
    var timer = null;

    if (msgs.length > 1 && msgEl) {
      var i = 0;
      timer = setInterval(function () {
        i = (i + 1) % msgs.length;
        msgEl.textContent = msgs[i];
        msgEl.style.animation = 'none';
        void msgEl.offsetWidth;
        msgEl.style.animation = '';
      }, speed);
    }

    var closeBtn = $('.announce__close', bar);
    if (closeBtn) closeBtn.addEventListener('click', function () {
      if (timer) clearInterval(timer);
      bar.hidden = true;
      store.set(dismissKey, '1');
    });
  })();

  /* ============================================================
     Sticky header — cream + shadow after 80px of scroll
     ============================================================ */
  (function () {
    var header = $('.site-header');
    if (!header) return;
    var ticking = false;
    function update() {
      ticking = false;
      header.classList.toggle('is-scrolled', window.scrollY > 80);
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  })();

  /* ============================================================
     Mobile menu
     ============================================================ */
  (function () {
    var btn = $('[data-menu-open]');
    var overlay = $('#mobile-menu');
    if (!btn || !overlay) return;
    btn.addEventListener('click', function () {
      btn.setAttribute('aria-expanded', 'true');
      openDialog(overlay, { onClose: function () { btn.setAttribute('aria-expanded', 'false'); } });
    });
  })();

  /* ============================================================
     Scroll reveal — IntersectionObserver PLUS a throttled scroll
     sweep so anything already at/above the fold reveals
     immediately (covers anchor jumps and mid-page reloads).
     ============================================================ */
  var reveal = (function () {
    function show(el, delay) {
      window.setTimeout(function () { el.classList.add('is-in'); }, delay || 0);
    }
    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { show(entry.target); io.unobserve(entry.target); }
        });
      }, { rootMargin: '0px 0px -8% 0px' });
    }
    function observe(el) {
      if (io) io.observe(el);
    }
    var queued = false;
    function sweep() {
      queued = false;
      var h = window.innerHeight;
      $$('.reveal:not(.is-in)').forEach(function (el, i) {
        if (el.getBoundingClientRect().top < h * 0.92) show(el, i * 80);
      });
    }
    function requestSweep() {
      if (!queued) { queued = true; requestAnimationFrame(sweep); }
    }
    $$('.reveal').forEach(observe);
    window.addEventListener('scroll', requestSweep, { passive: true });
    window.addEventListener('resize', requestSweep);
    window.setTimeout(sweep, 300);
    return { observe: observe, sweep: requestSweep };
  })();

  /* ============================================================
     Product card behaviour (event delegation, works for cards
     injected later by collection.js)
     ============================================================ */
  function sparkleBurst(host, count) {
    if (!host) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    count = count || 7;
    var frag = document.createDocumentFragment();
    for (var i = 0; i < count; i++) {
      var a = (i / count) * Math.PI * 2;
      var dot = document.createElement('span');
      dot.style.background = i % 2 ? '#B79FD4' : '#F5D06A';
      dot.style.setProperty('--dx', Math.cos(a) * 46 + 'px');
      dot.style.setProperty('--dy', (Math.sin(a) * 46 - 14) + 'px');
      frag.appendChild(dot);
    }
    host.innerHTML = '';
    host.appendChild(frag);
    window.setTimeout(function () { host.innerHTML = ''; }, 750);
  }

  /* The client's wishlist heart is gone from the card — it only toggled its own
     appearance and forgot the choice on reload, which promises the shopper something the
     theme cannot keep. Its handler goes with it; reinstate both alongside a wishlist app.
     Quick view now occupies that corner of the card. */
  document.addEventListener('click', function (e) {
    var quick = e.target.closest('[data-quick-add]');
    if (quick) {
      e.preventDefault();
      var card = quick.closest('.product-card');
      /* The variant id is rendered onto the button by snippets/trendout-product-card
         .liquid — the button's own attribute wins so a card with a size picker can
         quick-add the chosen variant rather than the card default. */
      var variantId = quick.getAttribute('data-variant-id') || (card && card.getAttribute('data-variant-id'));
      if (!variantId) {
        /* The product needs options chosen before it can be added. Rather than leaving
           the collection page, open this card's quick view so the shopper can pick a
           size and add without losing their place. Falling back to the product page only
           when quick view is unavailable. */
        var qvTrigger = card && card.querySelector('[data-quickview]');
        if (qvTrigger) {
          qvTrigger.click();
          return;
        }
        var href = card && card.querySelector('a[href]');
        if (href) window.location.href = href.getAttribute('href');
        return;
      }
      addToCart(variantId, 1, { openDrawer: true });
      sparkleBurst($('.sparkle-host', card));
      var label = quick.textContent;
      quick.classList.add('is-added');
      quick.textContent = '✓ Added';
      window.setTimeout(function () {
        quick.classList.remove('is-added');
        quick.textContent = label;
      }, 1800);
    }
  });

  /* ============================================================
     Homepage extras (no-ops elsewhere)
     ============================================================ */

  /* Countdown to a real deadline.
     The static build counted down from "2d 19h 35m" on every page load, so the offer
     never actually ended and the clock jumped back on refresh. It now counts to the
     timestamp the merchant set (data-countdown-until, epoch seconds). At zero the
     section either hides itself or swaps in an "offer ended" message, so an expired
     promotion cannot keep advertising itself. */
  $$('[data-countdown]').forEach(function (el) {
    var slots = {
      d: $('[data-cd-days]', el), h: $('[data-cd-hrs]', el),
      m: $('[data-cd-min]', el), s: $('[data-cd-sec]', el)
    };
    if (!slots.d || !slots.h || !slots.m || !slots.s) return;

    var until = parseInt(el.getAttribute('data-countdown-until'), 10);
    if (!until) return;

    var host = el.closest('[data-countdown-host]') || el;
    var endedText = el.getAttribute('data-countdown-ended') || '';
    var timer = null;

    function pad(n) { return String(n).padStart(2, '0'); }

    function finish() {
      if (timer) clearInterval(timer);
      if (endedText) {
        el.innerHTML = '<p class="countdown__ended">' + endedText + '</p>';
      } else {
        host.hidden = true;
      }
    }

    function tick() {
      var left = Math.max(0, until - Math.floor(Date.now() / 1000));
      if (left <= 0) { finish(); return; }
      slots.d.textContent = pad(Math.floor(left / 86400));
      slots.h.textContent = pad(Math.floor(left / 3600) % 24);
      slots.m.textContent = pad(Math.floor(left / 60) % 60);
      slots.s.textContent = pad(left % 60);
    }

    tick();
    timer = setInterval(tick, 1000);
  });

  /* Sold-this-month counter — eases up to its target on reveal */
  (function () {
    var el = $('[data-sold-counter]');
    if (!el) return;
    var target = parseInt(el.getAttribute('data-sold-counter'), 10) || 1284;
    var started = false;
    function run() {
      if (started) return;
      started = true;
      var t0 = performance.now();
      function step(t) {
        var p = Math.min(1, (t - t0) / 1400);
        el.textContent = Math.round(target * (1 - Math.pow(1 - p, 3))).toLocaleString('en-US');
        if (p < 1) requestAnimationFrame(step);
      }
      requestAnimationFrame(step);
    }
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) { run(); io.disconnect(); }
      });
      io.observe(el);
    } else { run(); }
  })();

  /* Generic accordion — any [data-accordion] button with aria-controls.

     The static build toggled the `hidden` attribute, which is display:none and therefore
     cannot animate — panels snapped open and shut. State now lives only on the button's
     aria-expanded, and trendout-overrides.css animates the panel from it. Closing a
     sibling and opening the clicked panel both happen in this one frame, so the two
     transitions run together at the same speed rather than one after the other.

     Collapsed panels are hidden with visibility, not display, so they stay out of the
     tab order and the accessibility tree exactly as `hidden` did. */
  document.addEventListener('click', function (e) {
    var toggle = e.target.closest('[data-accordion]');
    if (!toggle) return;
    var body = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!body) return;
    var open = toggle.getAttribute('aria-expanded') === 'true';
    /* single-open groups: close siblings inside [data-accordion-group] */
    var group = toggle.closest('[data-accordion-group]');
    if (group && !open) {
      $$('[data-accordion]', group).forEach(function (other) {
        if (other === toggle) return;
        other.setAttribute('aria-expanded', 'false');
      });
    }
    toggle.setAttribute('aria-expanded', String(!open));
  });

  /* Carousel arrow buttons — scroll the referenced rail by one "page" */
  $$('[data-rail-prev], [data-rail-next]').forEach(function (btn) {
    var id = btn.getAttribute('data-rail-prev') || btn.getAttribute('data-rail-next');
    var dir = btn.hasAttribute('data-rail-prev') ? -1 : 1;
    btn.addEventListener('click', function () {
      var rail = document.getElementById(id);
      if (rail) rail.scrollBy({ left: dir * rail.clientWidth * 0.8, behavior: 'smooth' });
    });
  });

  /* "Add set" bundle buttons.
     The static build passed "Title|price|image" and invented a cart line. The attribute
     now carries the real variant ids in the set — one for a single bundle product, or
     several to add every piece of the set in one request. */
  $$('[data-add-bundle]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var ids = (btn.getAttribute('data-add-bundle') || '').split(',');
      addItems(ids, { openDrawer: true });
    });
  });

  /* Newsletter — no JS.
     The static build intercepted submit and faked a success message without sending
     anything. The section now uses Shopify's {% form 'customer' %}, which posts for
     real and renders its own success and error states server-side after the redirect.
     Hijacking submit here would break that, so nothing is bound. */

  /* ============================================================
     Recently viewed — record only
     ============================================================
     Stores the ids of products this visitor has opened, most recent first, in their own
     browser. Nothing is sent anywhere and no personal data is involved. The rail that
     displays them re-fetches live product data, so only the id is worth keeping.
     ============================================================ */
  var RECENT_KEY = 'trendout-recently-viewed';
  var RECENT_MAX = 12;

  function recentlyViewed() {
    try {
      var raw = store.get(RECENT_KEY);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  }

  (function recordView() {
    var id = window.Trendout && window.Trendout.currentProductId;
    if (!id) return;
    var list = recentlyViewed().filter(function (x) { return String(x) !== String(id); });
    list.unshift(id);
    store.set(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  })();

  /* ---------- Public API ----------
     Merged into the object theme.liquid already put on window (cart, routes,
     moneyFormat, strings). Assigning a fresh object here would wipe that bootstrap
     data and leave the cart with no routes. */
  var api = {
    formatRs: formatRs,
    parseRs: parseRs,
    formatMoney: formatMoney,
    addToCart: addToCart,
    addItems: addItems,
    changeLine: changeLine,
    refreshCart: refreshCart,
    renderCartBadges: renderCartBadges,
    openCartDrawer: openCartDrawer,
    openDialog: openDialog,
    sparkleBurst: sparkleBurst,
    recentlyViewed: recentlyViewed,
    reveal: reveal
  };
  window.Trendout = window.Trendout || {};
  Object.keys(api).forEach(function (k) { window.Trendout[k] = api[k]; });
})();
